<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { MENU_VISIBILITY_CATALOG } from 'next/sidebar/menuVisibility/catalog';
import SidebarMenuVisibilityAPI from 'next/sidebar/menuVisibility/api';

const ROLES = [
  { value: 'administrator', label: 'Administrador' },
  { value: 'agent', label: 'Agente' },
];

const isLoading = ref(true);
const isSaving = ref(false);

// config[key] = ['administrator', 'agent'] -> lista de roles que podem ver
// o item `key`. Ausência de chave = visível para todos.
const config = reactive({});

const isChecked = (key, role) => {
  const roles = config[key];
  // Sem configuração salva ainda = tudo visível por padrão.
  if (!roles) return true;
  return roles.includes(role);
};

const toggle = (key, role) => {
  const current = config[key] || ROLES.map(r => r.value);
  config[key] = current.includes(role)
    ? current.filter(r => r !== role)
    : [...current, role];
};

const loadConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await SidebarMenuVisibilityAPI.get();
    Object.assign(config, data?.config || {});
  } catch (error) {
    useAlert('Não foi possível carregar a configuração de visibilidade');
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await SidebarMenuVisibilityAPI.update({ ...config });
    Object.keys(config).forEach(key => delete config[key]);
    Object.assign(config, data?.config || {});
    useAlert('Configuração salva com sucesso');
  } catch (error) {
    useAlert('Não foi possível salvar a configuração de visibilidade');
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadConfig);
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 overflow-y-auto bg-n-background p-6">
    <header class="flex items-center justify-between flex-shrink-0 mb-4">
      <div>
        <h1 class="text-base font-medium text-n-slate-12 m-0">
          Visibilidade de menus
        </h1>
        <p class="text-sm text-n-slate-11 m-0 max-w-xl">
          Escolha quais itens e submenus da barra lateral cada papel pode
          ver. Itens sem nenhuma marcação continuam visíveis para todos.
        </p>
      </div>
      <Button
        label="Salvar"
        size="sm"
        :is-loading="isSaving"
        :disabled="isLoading"
        @click="save"
      />
    </header>

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <table v-else class="w-full text-sm border-collapse">
      <thead>
        <tr class="border-b border-n-weak">
          <th class="text-left py-2 font-medium text-n-slate-11">Item</th>
          <th
            v-for="role in ROLES"
            :key="role.value"
            class="text-center py-2 font-medium text-n-slate-11 w-32"
          >
            {{ role.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="item in MENU_VISIBILITY_CATALOG" :key="item.key">
          <tr class="border-b border-n-weak">
            <td class="py-2 font-medium text-n-slate-12">{{ item.label }}</td>
            <td v-for="role in ROLES" :key="role.value" class="text-center py-2">
              <Checkbox
                :model-value="isChecked(item.key, role.value)"
                @change="() => toggle(item.key, role.value)"
              />
            </td>
          </tr>
          <tr
            v-for="child in item.children || []"
            :key="child.key"
            class="border-b border-n-weak"
          >
            <td class="py-2 pl-6 text-n-slate-11">{{ child.label }}</td>
            <td v-for="role in ROLES" :key="role.value" class="text-center py-2">
              <Checkbox
                :model-value="isChecked(child.key, role.value)"
                @change="() => toggle(child.key, role.value)"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
